import classnames from 'classnames';
import { FieldState } from 'formstate';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { StandardTextFieldProps } from '@material-ui/core/TextField';
import { TextField } from '@material-ui/core';

import { rootContainer } from 'src/ioc';
import { IosFocusStore } from '../stores/ios-focus.store';

interface FieldProps extends StandardTextFieldProps {
    fieldState: FieldState<string>;
    errorMap?: { [error: string]: string | null };
    startAdornment?: React.ReactNode;
    endAdornment?: React.ReactNode;
    endAdornmentFixed?: boolean;
    errorColor?: boolean;
    formatter?: (value: string) => string;
    validator?: (value: string) => boolean;
    maxLength?: number;
    readOnly?: boolean;
}

const iosFocus = rootContainer.get(IosFocusStore);

@observer
export class MatField extends React.Component<FieldProps, {}> {
    @observable private hadBlur = false;
    private hadBlurTimeout: any;
    private hasFocus = false;
    @observable private fixAdornment = false;

    componentWillReceiveProps(nextProps: Readonly<FieldProps>) {
        const props = this.props;
        if (props.label !== nextProps.label || props.placeholder !== nextProps.placeholder) {
            // If label or placeholder changes, treat this as a new field.
            // (Useful for create / confirm password, for example.)
            clearTimeout(this.hadBlurTimeout);
            this.hadBlur = false;
            props.fieldState.dirty = false;
        }
    }

    private handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.hasFocus = true;
        if (!this.props.readOnly) {
            iosFocus.handleFocus();
        }
        if (this.props.onFocus) {
            this.props.onFocus(e);
        }
    };

    private handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.hasFocus = false;
        this.fixAdornment = false;
        if (!this.props.readOnly) {
            iosFocus.handleBlur();
        }
        this.hadBlurTimeout = setTimeout(
            () => {
                this.hadBlur = true;
                this.props.fieldState.dirty = true;
            },
            200 // 200ms from FieldState default validation delay
        );
        if (this.props.onBlur) {
            this.props.onBlur(e);
        }
    };

    componentWillUnmount() {
        if (this.hasFocus) {
            iosFocus.handleBlur();
        }
    }

    private handleAdornmentEnter = () => {
        if (!this.hasFocus) {
            this.fixAdornment = true;
        }
    };

    private handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { fieldState, formatter, validator } = this.props;
        if (!validator || validator(e.target.value)) {
            fieldState.onChange(formatter ? formatter(e.target.value) : e.target.value);
        }
        if (fieldState.dirty && !this.hadBlur) {
            fieldState.dirty = false;
        }
    };

    render() {
        const {
            fieldState,
            errorMap,
            startAdornment,
            endAdornment: endAdornmentOrig,
            endAdornmentFixed,
            errorColor,
            maxLength,
            readOnly,
            ...props
        } = this.props;
        const errorText =
            fieldState.dirty &&
            fieldState.error &&
            (!errorMap || errorMap[fieldState.error] !== null)
                ? (errorMap && errorMap[fieldState.error]) || fieldState.error
                : null;
        const endAdornment = endAdornmentOrig ? (
            <div
                className={classnames('endAdornment', {
                    endAdornmentFixed: this.fixAdornment || endAdornmentFixed,
                })}
                onMouseEnter={this.handleAdornmentEnter}
            >
                {endAdornmentOrig}
            </div>
        ) : null;

        // 200ms delay from https://github.com/formstate/formstate/blob/master/src/core/fieldState.ts#L65
        return (
            <TextField
                {...props}
                value={fieldState.value}
                onChange={this.handleChange}
                onFocus={this.handleFocus}
                onBlur={this.handleBlur}
                FormHelperTextProps={{ error: true }}
                helperText={errorText}
                InputProps={{ startAdornment, endAdornment, inputProps: { maxLength, readOnly } }}
                error={errorColor ? !!errorText : false}
            />
        );
    }
}
